import React from 'react';
import { CarlineWithOffers } from 'src/types';
import { OffersTrackingLevel } from 'src/feature-app/tracking-context';
import {
  ContentLabel,
  Model,
  OffersTrackingEvent,
  OffersTrackingManager,
  TrackingCalculation,
  TrackingConfiguration,
  TrackingEvent,
  TrackingEventInfo,
  TrackingLink,
  TrackingOwnership,
  TrackingPartner,
  TrackingPersonalization,
  TrackingProcess,
} from 'src/feature-app/tracking-types';
import { useFeatureServices } from './use-feature-services';
import { useGetBrand } from 'src/feature-app';

const emptyPriceConfiguration: TrackingConfiguration = {
  PriceType: 'price',
  PriceTypeParameter: undefined,
  PriceRateCurrency: undefined,
  PriceRateBasic: undefined,
  PriceRateAdditional: undefined,
  PriceRateTotal: undefined,
  PriceTotalCurrency: undefined,
  PriceTotalBasic: undefined,
  PriceTotalAdditional: undefined,
  PriceTotalTotal: undefined,
};

const emptyModelConfig: TrackingConfiguration = {
  BodyTypeName: undefined,
  SalesgroupId: undefined,
  SalesgroupName: undefined,
  EquipmentlineName: undefined,
  ModelId: undefined,
  ModelName: undefined,
  ModelYear: undefined,
};

const emptyConfig = { ...emptyPriceConfiguration, ...emptyModelConfig };

const emptyCalculation: TrackingCalculation = {
  FinancingCurrency: 'EUR',
  FinancingInterest: undefined,
  FinancingPaymentMethod: undefined,
};

const emptyPartner: TrackingPartner = {
  informationBnr: undefined,
  informationName: undefined,
  informationDepartment: undefined,
  informationZIP: undefined,
};

const personalization: TrackingPersonalization = {
  status: false,
  placeholderId: undefined,
  contentId: undefined,
  activityId: undefined,
  activityName: undefined,
  experienceId: undefined,
  experienceName: undefined,
  offerId: undefined,
  offerName: undefined,
};

const baseEventInfo: TrackingEventInfo = {
  brand: 'passenger',
  pageCategory: 'Ofertas',
  sectionName: undefined,
  templateType: 'Feature App Template',
};

const getBaseEventInfo = (brand: 'V' | 'N') => {
  return { ...baseEventInfo, brand: brand === 'N' ? 'commercial' : 'passenger' };
};

const emptyOwnership: TrackingOwnership = {
  vehicle: undefined,
};

const defaultTrackingEvent: OffersTrackingEvent = {
  personalization,
};

// User-Defined Type Guards
function isCarlineWithOffers(model: Model): model is CarlineWithOffers {
  return (model as CarlineWithOffers).code !== undefined;
}

function createTrackingConfiguration(
  model: Model,
  config?: TrackingConfiguration,
  withoutPrice?: boolean
): TrackingConfiguration | undefined {
  if (isCarlineWithOffers(model)) {
    return {
      CarlineId: String(model.code),
      CarlineName: model.title,
      ...emptyConfig,
    };
  } else {
    const emptyConfigObj = withoutPrice ? emptyModelConfig : emptyConfig; // FormFieldClick no necesita los datos de Price.

    const PriceRateType = model.myRenting ? 'My Renting' : model.next ? 'Next' : undefined;

    return {
      CarlineId: String(model.carline.code),
      CarlineName: model.carline.title,
      ...emptyConfigObj, // Sobreescribe lo necesario
      PriceRateType,
      ...config,
    };
  }
}

function createTrackingLink(model: Model, form?: string): TrackingLink | undefined {
  const link = {
    url: window ? window.location.href : undefined,
  };
  if (isCarlineWithOffers(model)) {
    return { ...link, name: `ofertas:${model.name}${form ? `:${form}` : ''}` };
  } else {
    return { ...link, name: `ofertas:${model.carline.name}${form ? `:${form}` : ''}` };
  }
}

function createTrackingProcess(level: OffersTrackingLevel): TrackingProcess {
  return {
    StepName: level.levelName,
    StepNumber: String(level.levelNumber),
  };
}

function getContentLabel(model: Model): ContentLabel {
  return {
    models: [isCarlineWithOffers(model) ? model.name : model.carline.name],
  };
}

// Algunos eventos no necesitan esta información de la configuración y
// es más facil delvolver siempre el objeto completo y eliminar las partes que no necesitamos.

function deleteModelData(
  config: TrackingConfiguration,
  deleteName: boolean,
  deleteYear: boolean,
  deleteId: boolean
): TrackingConfiguration {
  const newConfig = { ...config };
  deleteName ? delete newConfig.ModelName : null;
  deleteYear ? delete newConfig.ModelYear : null;
  deleteId ? delete newConfig.ModelId : null;
  deleteId ? delete newConfig.BodyTypeName : null; // Está en los mismos eventos.
  return newConfig;
}

export function useTrackingManager(): OffersTrackingManager {
  const { tracking: trackingManager } = useFeatureServices();
  const brand = useGetBrand();

  return React.useMemo((): OffersTrackingManager => {
    function track(action: string, event: OffersTrackingEvent): void {
      if (trackingManager) {
        trackingManager.track(action, event as TrackingEvent);
      }
    }

    return {
      trackOffersCloseClick(): void {
        track('VWBasic_Close_Click', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand) },
        });
      },
      trackOffersContentSectionEnterViewport(model): void {
        track('VWBasic_ContentSection_EnterViewport', {
          ...defaultTrackingEvent,
          configuration: createTrackingConfiguration(model),
        });
      },

      trackOffersModelSelectClick(model, eventInfo, config?): void {
        track('VWBasic_ModelSelect_Click', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo },
          configuration: createTrackingConfiguration(model, { ...config }),
          partner: emptyPartner,
          link: createTrackingLink(model),
        });
      },

      trackOffersFormCTAClick(model, form, eventInfo, partner, configInfo?): void {
        let configuration = createTrackingConfiguration(model, { ...emptyPriceConfiguration, ...configInfo });
        configuration = deleteModelData(configuration, true, true, true);

        track('VWBasic_FormCTA_Click', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo },
          configuration,
          link: createTrackingLink(model, form.FormName),
          form,
          partner,
          ownership: emptyOwnership,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },

      trackOffersFormSubmissionSuccessMessageLoad(model, form, eventInfo, partner?, configInfo?): void {
        let configuration = createTrackingConfiguration(model, { ...emptyPriceConfiguration, ...configInfo });
        configuration = deleteModelData(configuration, true, true, true);

        track('VWBasic_FormSubmissionSuccessMessage_Load', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo },
          configuration,
          form,
          partner,
          ownership: emptyOwnership,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },

      trackOffersFormLayerLoad(model, form, eventInfo, configInfo?): void {
        let configuration = createTrackingConfiguration(model, { ...emptyPriceConfiguration, ...configInfo });
        configuration = deleteModelData(configuration, true, true, true);

        track('VWBasic_FormLayer_Load', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo, contentLabels: getContentLabel(model) },
          configuration,
          form,
          partner: emptyPartner,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },

      trackOffersFormFieldClick(model, form, stepNumber, eventInfo, partner?, configInfo?): void {
        let configuration = createTrackingConfiguration(model, { ...emptyPriceConfiguration, ...configInfo });
        configuration = deleteModelData(configuration, true, true, true);

        track('VWBasic_FormField_Click', {
          ...defaultTrackingEvent,
          configuration,
          form,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo },
          process: {
            StepName: 'FormData',
            StepNumber: stepNumber,
          },
          partner,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },

      trackOffersVehicleButtonClick(model, eventInfo, config?, linkInfo?): void {
        console.log('trackOffersVehicleButtonClick');
        let configuration = createTrackingConfiguration(model, config);
        configuration = deleteModelData(configuration, false, true, false);

        track('VWBasic_VehicleButton_Click', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo },
          configuration,
          link: linkInfo ? linkInfo : createTrackingLink(model),
          partner: emptyPartner,
          calculation: emptyCalculation,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },

      trackOffersVehicleLinkClick(model, eventInfo): void {
        track('VWBasic_VehicleLink_Click', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo },
          configuration: createTrackingConfiguration(model),
          link: createTrackingLink(model),
          partner: emptyPartner,
          calculation: emptyCalculation,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },

      trackOffersSliderElementLoad(model, eventInfo, config?): void {
        let configuration = createTrackingConfiguration(model, config);
        configuration = deleteModelData(configuration, false, true, false);

        track('VWBasic_VehicleSliderElement_Load', {
          ...defaultTrackingEvent,
          eventInfo: { ...getBaseEventInfo(brand), ...eventInfo, contentLabels: getContentLabel(model) },
          configuration,
          link: createTrackingLink(model),
          partner: emptyPartner,
          calculation: emptyCalculation,
          environment: { featureAppIdAppVersion: process.env.VERSION },
        });
      },
    };
  }, [trackingManager]);
}
